import React, { useState } from 'react';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Vendor from 'data/Vendor';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import SelectComponent from 'components/UI/Select';
import { useEmailSettings } from 'hooks/useEmailSettings';
import { getItems } from 'lib/SessionStorage';
import { SHRole } from 'data/User';
import { EmailPermission } from 'data/EmailPermissions';
import { Accordion, AccordionSummary, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButton from 'components/UI/RadioButton';
import { Background, Border, Main, Shade, Text } from 'styles/themeColors';
import { updateEmailPermissions } from 'requests/emailPermissions';
import { useAuth } from 'lib/Auth';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { FILTERED_USER_ROLES, USER_ROLE_MAPPER } from 'constants/user';
import { FontWeight } from 'lib/styles';
import { STORAGE_KEYS } from 'constants/general';

interface EmailSettingsProps {
    setIsSaveDisabled: (disabled: boolean) => void;
    setHasUnsavedChanges: (hasChanges: boolean) => void;
    setReadOnly?: (readOnly: boolean) => void;
    selectedVendor: Vendor;
    disabled?: boolean;
}

// Main component
const EmailSettings = React.forwardRef<
    { handleSave: () => void },
    EmailSettingsProps
>(({ setIsSaveDisabled, setHasUnsavedChanges, setReadOnly, selectedVendor }, ref) => {
    let roles = getItems(STORAGE_KEYS.ROLES);

    roles = roles?.filter((role: any) => !FILTERED_USER_ROLES.includes(role.name)) || [];

    const [expandedPanel, setExpandedPanel] = useState<string | false>('panel-0');
    const [selectedRole, setSelectedRole] = useState<SHRole>();
    const [editedPermissions, setEditedPermissions] = useState<EmailPermission[]>([]);

    const { token } = useAuth();

    const {
        permissions,
        loading,
        handleEdit,
        refreshData,
    } = useEmailSettings({
        roleId: selectedRole?.id!,
        setEditedPermissions,
        setHasUnsavedChanges
    });

    const handleSave = async () => {
        try {
            setIsSaveDisabled(true);

            // Transform edited permissions to API format
            const updates = editedPermissions.map(permission => ({
                roleId: permission.roleId,
                emailPermissionId: permission.id,
                active: permission.active
            }));

            const response = await updateEmailPermissions(updates, { token });

            if (response) {
                renderToast(toast.TYPE.SUCCESS, 'Email settings updated successfully');
                // Clear edited permissions since they're now saved
                setEditedPermissions([]);
                refreshData(); // This will reset original permissions and unsaved changes state
            }
        } catch (err: any) {
            console.error('Error updating email settings:', err);
            renderToast(toast.TYPE.ERROR, err?.message || 'Failed to update email settings');
        } finally {
            setIsSaveDisabled(false);
        }
    }

    // Expose save method to parent component via ref
    React.useImperativeHandle(ref, () => ({
        handleSave
    }));

    const handleAccordionChange = (panel: string) => (_: any, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    return (
        <FlexboxContainerComponent flexDirection="column" margin='0px 0px 20px 0px'>
            <SelectComponent
                options={roles?.map((role: any) => ({
                    value: USER_ROLE_MAPPER[role.name],
                    id: role.id
                }))}
                value={USER_ROLE_MAPPER[selectedRole?.name!]}
                defaultValue={'Please select user role'}
                onChange={(e) => setSelectedRole(roles.find((role: any) => role.id === e.target.id))}
                width="400px"
                className='cursor-pointer'
                labelPosition='top'
                label='User role'
            />

            {loading ? (
                <LoadingIndicator center />
            ) : (
                <>
                    {/* Render Accordions with Details Section Immediately After Expanded One */}
                    {permissions.map((permission, index: number) => (
                        <React.Fragment key={index.toString()}>
                            <Accordion
                                expanded={expandedPanel === `panel-${index}`}
                                onChange={handleAccordionChange(`panel-${index}`)}
                                style={{ marginTop: 16, borderRadius: 6, border: `1px solid ${Border.main}` }}
                                className="no-top-border"
                            >
                                <AccordionSummary
                                    style={{
                                        backgroundColor: Shade.shade020,
                                        height: 68,
                                        minHeight: 68,
                                        maxHeight: 68,
                                        borderRadius: 8,
                                        borderColor: Border.main
                                    }}
                                    expandIcon={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <ExpandMoreIcon />
                                        </div>
                                    }
                                    aria-controls={`panel-${index}-content`}
                                >
                                    <div className='heading' style={{ fontSize: 20 }}>
                                        {permission.group.name}
                                    </div>
                                </AccordionSummary>
                            </Accordion>

                            {/* Details Section - Render immediately after expanded accordion */}
                            {expandedPanel === `panel-${index}` && (
                                <div style={{
                                    backgroundColor: 'white'
                                }}>
                                    {/* Header Row */}
                                    <div style={{
                                        flexDirection: 'row',
                                        display: 'flex',
                                        width: '100%',
                                        padding: '10px 40px',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom: `1px solid ${Border.light}`
                                    }}>
                                        <div></div>
                                        <FlexboxContainerComponent gap='50px'>
                                            <Typography style={{ fontSize: 16, color: Text.secondary, fontWeight: FontWeight.bold }}>
                                                Send
                                            </Typography>
                                            <Typography style={{ fontSize: 16, color: Text.secondary, fontWeight: FontWeight.bold }}>
                                                Disable
                                            </Typography>
                                        </FlexboxContainerComponent>
                                    </div>

                                    {/* Permission Rows */}
                                    {permission.data?.map((emailPermission: EmailPermission, permIndex: number) => (
                                        <div
                                            key={permIndex}
                                            style={{
                                                flexDirection: 'row',
                                                display: 'flex',
                                                width: '100%',
                                                backgroundColor: Background.shaded,
                                                padding: '10px 40px',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                borderBottom: permIndex < permission.data.length - 1 ? `1px solid ${Border.light}` : 'none'
                                            }}
                                        >
                                            <Typography style={{ fontSize: 16 }}>
                                                {emailPermission.name}
                                            </Typography>
                                            <FlexboxContainerComponent gap='50px'>
                                                <RadioButton
                                                    name=''
                                                    value=''
                                                    id=''
                                                    checked={emailPermission.active}
                                                    onChange={(value) => handleEdit(emailPermission, value)}
                                                    color={Main.brandLight}
                                                />
                                                <RadioButton
                                                    name=''
                                                    value=''
                                                    id=''
                                                    checked={!emailPermission.active}
                                                    onChange={(value) => handleEdit(emailPermission, !value)}
                                                    color={Main.brandLight}
                                                />
                                            </FlexboxContainerComponent>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )}
        </FlexboxContainerComponent>
    );
});

export default EmailSettings;